/**
 * Phase 4B-2 — data layer for the customer's own bot.
 *
 * Every call goes through /api/tenant/bot, where the gateway resolves the
 * tenant from the session, so nothing here takes a tenant id and no screen can
 * be tricked into addressing another customer's bot.
 *
 * POLLING CADENCE — this is the number the customer feels.
 *
 * The approved dashboard (public/app.js startPolling) fetched /state, /history,
 * /signals/queue AND /alerts every 2 000 ms, all four, always. 4B-2 shipped
 * 5 s / 20 s / 10 s / 15 s. That alone meant a settled trade could take up to
 * 20 s to reach History and 5 s to move the balance, where before it was ≤2 s —
 * which is the "results are slower than before" report, independent of
 * anything on the server. Restored to the approved 2 s on every surface.
 *
 * The gateway micro-caches /state per tenant for 2 s and coalesces concurrent
 * misses, so N open dashboards cost the bot at most one live Chromium read per
 * 2 s — the same rate the approved single dashboard produced with no cache.
 * /signals/queue and /alerts are in-memory (~1 ms). /history?filter=today is
 * O(records) in the bot (frozen daykey.ts builds an Intl.DateTimeFormat per
 * record): ~1 ms at a normal day's volume, tens of ms at the 1000-record cap —
 * the same cost the approved dashboard paid every 2 s.
 */
import { useEffect, useRef } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type BotState, type MartingaleSettings, type SafetySettings } from '@/lib/api';

/** The approved dashboard's pollMs. Every live surface refreshes at this rate. */
export const POLL_MS = 2000;

export const botKeys = {
  state: ['bot', 'state'] as const,
  alerts: ['bot', 'alerts'] as const,
  queue: ['bot', 'queue'] as const,
  history: (filter: string) => ['bot', 'history', filter] as const,
  proStatus: ['bot', 'pro-status'] as const,
};

/** Live bot state. Polled, because the balance and P&L change on their own. */
export function useBotState(enabled = true) {
  return useQuery({
    queryKey: botKeys.state,
    queryFn: api.botState,
    enabled,
    refetchInterval: POLL_MS,
    retry: false,
  });
}

/**
 * PRO automatic mode's live analysis: per-pair bars, trend, payout and the one
 * reason each pair is or is not eligible right now.
 *
 * Polled at the same 2 s as every other live surface. It carries the payout per
 * charted pair itself rather than the dashboard also fetching /payout-census:
 * measured 2026-09-21, this response is 386 bytes against the census's 12,408,
 * and the census lists all 183 assets to answer a question about four.
 *
 * `retry: false` matches useBotState — a bot without the mode answers
 * {enabled:false} rather than an error, and a genuinely unreachable bot should
 * surface immediately instead of after three silent retries.
 */
export function useProStatus(enabled = true) {
  return useQuery({
    queryKey: botKeys.proStatus,
    queryFn: api.botProStatus,
    enabled,
    refetchInterval: POLL_MS,
    retry: false,
  });
}

/** Health + recent alerts. Drives the Connected badge. */
export function useBotAlerts(enabled = true) {
  return useQuery({
    queryKey: botKeys.alerts,
    queryFn: api.botAlerts,
    enabled,
    refetchInterval: POLL_MS,
    retry: false,
  });
}

export function useBotHistory(filter: string, enabled = true, intervalMs = POLL_MS) {
  return useQuery({
    queryKey: botKeys.history(filter),
    queryFn: () => api.botHistory(filter),
    enabled,
    refetchInterval: intervalMs,
    retry: false,
    // Today → Week → All is a new key each time; without this the whole page
    // flashed to "Loading…" and the filter buttons vanished on every switch.
    placeholderData: keepPreviousData,
  });
}

export function useBotQueue(enabled = true) {
  return useQuery({
    queryKey: botKeys.queue,
    queryFn: api.botQueue,
    enabled,
    refetchInterval: POLL_MS,
    retry: false,
  });
}

/** True when a write's response carries the bot's fresh state snapshot. */
function carriesState(r: unknown): r is { state: BotState } {
  return (
    typeof r === 'object' &&
    r !== null &&
    'state' in r &&
    typeof (r as { state?: unknown }).state === 'object' &&
    (r as { state?: { control?: unknown } }).state?.control !== undefined
  );
}

/**
 * Any write can change what /state reports, so they all refresh it. The
 * gateway also drops its own cached /state on a write, so the next poll is
 * genuinely fresh rather than up to a few seconds stale.
 *
 * INSTANT TOGGLE: /control/pause and /control/resume answer with the new
 * state, and the approved dashboard painted that response on the spot
 * (renderSnapshot(r.body.state)) — the switch flipped the moment the bot
 * confirmed. We do the same: write the returned snapshot into the cache
 * before the refetch, so START/STOP never waits on a poll.
 */
function useBotMutation<TResult, TArgs = void>(
  fn: (args: TArgs) => Promise<TResult>,
  // Query keys are `as const`, so this must accept readonly arrays or every
  // call site fails to assign.
  also: ReadonlyArray<readonly unknown[]> = []
) {
  const qc = useQueryClient();
  return useMutation<TResult, Error, TArgs>({
    mutationFn: fn,
    onSuccess: async (r) => {
      if (carriesState(r)) {
        // A poll that was already in flight when the write completed carries
        // the PRE-write snapshot; if it resolved after this it would overwrite
        // the fresh one and the switch would visibly bounce. Cancel it first.
        await qc.cancelQueries({ queryKey: botKeys.state });
        qc.setQueryData(botKeys.state, r.state);
      }
      void qc.invalidateQueries({ queryKey: botKeys.state });
      for (const key of also) void qc.invalidateQueries({ queryKey: key });
    },
  });
}

/**
 * Make the Dashboard's numbers land as fast as History's.
 *
 * /history is never cached by the gateway; /state is (for 2 s, because it does
 * a live Chromium balance read). So the customer saw a WIN in History before
 * the Dashboard moved, and asked why. This watches the history the screen
 * already polls and, the moment a new settled trade appears, refetches /state
 * instead of waiting for the next tick. The gateway drops its cached snapshot
 * on the same signal, so the refetch is genuinely fresh rather than the stale
 * one it just decided was wrong.
 *
 * Bounded on purpose: it fires on a CHANGE, so at most one extra read per
 * settled trade (~1 per 70 s) against a ~30/min polling baseline, and it lands
 * just after a settlement — most of a candle away from the ~100 ms prep window
 * before the next entry click. It never fires on a poll that changed nothing.
 */
export function useSettlementRefresh(): void {
  const qc = useQueryClient();
  // Same key the History screen uses, so this is a shared subscription, not a
  // second poll, whenever both are mounted.
  // Watched at 1 s, not 2 s, ON PURPOSE. /history is in-memory in the bot
  // (~1 ms) and never cached, so this is the cheap half of the pair. Polling it
  // at the SAME 2 s as /state left the two in phase: the settle was noticed on
  // the very tick /state was about to run anyway, so forcing a refetch bought
  // nothing (measured: 2.35 s from settle to the screen). Halving only the
  // detector puts the Dashboard ~1 s ahead of its own tick without touching the
  // /state cadence, so the number of live Chromium reads is unchanged.
  // TanStack takes the SMALLEST interval across observers of a key, so the
  // History screen still polls at 2 s when this hook is not mounted.
  const history = useBotHistory('today', true, 1000);
  const items = history.data?.items;
  const marker =
    items && items.length > 0 ? `${history.data?.count ?? items.length}:${items[0]!.id}` : '';
  const seen = useRef<string | null>(null);
  useEffect(() => {
    if (!marker) return;
    const prev = seen.current;
    seen.current = marker;
    // Skip the first observation: that is just "the page loaded", not a settle.
    if (prev === null || prev === marker) return;
    void qc.invalidateQueries({ queryKey: botKeys.state });
  }, [marker, qc]);
}

export const usePauseBot = () => useBotMutation(() => api.botPause());
export const useResumeBot = () => useBotMutation(() => api.botResume());
export const useSetPayout = () => useBotMutation((pct: number) => api.botSetPayout(pct));
export const useSetMartingale = () => useBotMutation((p: Partial<MartingaleSettings>) => api.botSetMartingale(p));
export const useSetSafety = () => useBotMutation((p: Partial<SafetySettings>) => api.botSetSafety(p));
export const useSetRateLimit = () => useBotMutation((p: { tradesPerHour: number }) => api.botSetRateLimit(p));
/** Toggling selection also refreshes the analysis panel, which shows the state. */
export const useSetProAuto = () =>
  useBotMutation((enabled: boolean) => api.botSetProAuto(enabled), [botKeys.proStatus]);
export const useSwitchAccount = () => useBotMutation((t: 'demo' | 'real') => api.botSwitchAccount(t));
export const useResetLadder = () => useBotMutation(() => api.botResetLadder());
export const useResetDay = () => useBotMutation(() => api.botResetDay(), [botKeys.history('today')]);

/**
 * Placing a signal BLOCKS until the trade has settled (place, wait out the
 * expiry, read the outcome), so a 60s trade routinely takes 70-80s end to end.
 * Callers must show a busy state rather than assume a hung request.
 */
export const usePlaceSignal = () =>
  useBotMutation(
    (input: { asset: string; direction: 'BUY' | 'SELL'; expirySeconds: number; amount?: number; martingaleEnabled?: boolean }) =>
      api.botSignal(input),
    [botKeys.history('today'), botKeys.queue]
  );

export const useQueueBatch = () => useBotMutation((text: string) => api.botQueueBatch(text), [botKeys.queue]);
export const useClearQueue = () => useBotMutation(() => api.botClearQueue(), [botKeys.queue]);
export const useCancelQueued = () => useBotMutation((id: string) => api.botCancelQueued(id), [botKeys.queue]);
export const useClearHistory = () =>
  useBotMutation(() => api.botClearHistory(), [botKeys.history('today'), botKeys.history('all')]);
